import { useState, useRef, useEffect } from 'react';

interface AreaChartProps {
  data: { label: string; value: number; value2?: number }[];
  height?: number;
  color?: string;
  color2?: string;
  currency?: string;
  label1?: string;
  label2?: string;
  showArea?: boolean;
}

export default function AreaChart({
  data,
  height = 220,
  color = '#0d9488',
  color2 = '#ef4444',
  currency = '$',
  label1 = 'Gross',
  label2 = 'Fees',
  showArea = true,
}: AreaChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(500);
  const [hovered, setHovered] = useState<number | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(containerRef.current);
    setContainerWidth(containerRef.current.clientWidth);
    return () => observer.disconnect();
  }, []);

  if (data.length === 0) return null;

  const hasSecondLine = data.some(d => d.value2 !== undefined);
  const allValues = data.flatMap(d => hasSecondLine && d.value2 !== undefined ? [d.value, d.value2] : [d.value]);
  const maxVal = Math.max(...allValues, 1);
  const minVal = Math.min(...allValues, 0);
  const range = maxVal - minVal || 1;

  const padding = { top: 30, bottom: 44, left: 54, right: 16 };
  const chartWidth = Math.max(containerWidth, 300);
  const chartHeight = height;

  function toX(i: number) {
    return padding.left + (i / Math.max(data.length - 1, 1)) * (chartWidth - padding.left - padding.right);
  }

  function toY(val: number) {
    return padding.top + (1 - (val - minVal) / range) * (chartHeight - padding.top - padding.bottom);
  }

  const points1 = data.map((d, i) => `${toX(i)},${toY(d.value)}`).join(' ');
  const area1 = `${toX(0)},${chartHeight - padding.bottom} ${points1} ${toX(data.length - 1)},${chartHeight - padding.bottom}`;

  let points2 = '';
  let area2 = '';
  if (hasSecondLine) {
    points2 = data.map((d, i) => `${toX(i)},${toY(d.value2 ?? 0)}`).join(' ');
    area2 = `${toX(0)},${chartHeight - padding.bottom} ${points2} ${toX(data.length - 1)},${chartHeight - padding.bottom}`;
  }

  const gridLines = [0, 0.25, 0.5, 0.75, 1];

  function formatVal(v: number) {
    if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
    return v.toFixed(0);
  }

  return (
    <div ref={containerRef} className="w-full overflow-x-auto">
      <svg width={chartWidth} height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
        <defs>
          <linearGradient id="areaGrad1" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.2" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
          {hasSecondLine && (
            <linearGradient id="areaGrad2" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color2} stopOpacity="0.15" />
              <stop offset="100%" stopColor={color2} stopOpacity="0.02" />
            </linearGradient>
          )}
        </defs>

        {gridLines.map((frac) => {
          const val = minVal + frac * range;
          const y = toY(val);
          return (
            <g key={frac}>
              <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y} stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray={frac === 0 ? '0' : '4'} />
              <text x={padding.left - 6} y={y + 4} textAnchor="end" fill="rgba(255,255,255,0.7)" fontSize="10">
                {formatVal(val)}
              </text>
            </g>
          );
        })}

        {showArea && <polygon points={area1} fill="url(#areaGrad1)" />}
        <polyline points={points1} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />

        {hasSecondLine && showArea && <polygon points={area2} fill="url(#areaGrad2)" />}
        {hasSecondLine && <polyline points={points2} fill="none" stroke={color2} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" strokeDasharray="6 3" />}

        {data.map((d, i) => {
          const isHov = hovered === i;
          return (
            <g key={i} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)} style={{ cursor: 'pointer' }}>
              <line x1={toX(i)} y1={padding.top} x2={toX(i)} y2={chartHeight - padding.bottom} stroke={isHov ? 'rgba(255,255,255,0.2)' : 'transparent'} strokeWidth="1" />
              <circle cx={toX(i)} cy={toY(d.value)} r={isHov ? 5 : 3.5} fill="white" stroke={color} strokeWidth="2" />
              {hasSecondLine && d.value2 !== undefined && (
                <circle cx={toX(i)} cy={toY(d.value2)} r={isHov ? 4 : 2.5} fill="white" stroke={color2} strokeWidth="1.5" />
              )}
              {isHov && (
                <>
                  <rect x={toX(i) - 40} y={Math.max(4, toY(d.value) - 30)} width={80} height={hasSecondLine ? 30 : 18} rx={4} fill="#1e293b" opacity="0.92" />
                  <text x={toX(i)} y={Math.max(4, toY(d.value) - 30) + 13} textAnchor="middle" fill="#ffffff" fontSize="10" fontWeight="600">
                    {currency}{(d.value / 100).toFixed(2)}
                  </text>
                  {hasSecondLine && d.value2 !== undefined && (
                    <text x={toX(i)} y={Math.max(4, toY(d.value) - 30) + 25} textAnchor="middle" fill="#fca5a5" fontSize="9">
                      Fee: {currency}{(d.value2 / 100).toFixed(2)}
                    </text>
                  )}
                </>
              )}
              {i % Math.ceil(data.length / 10) === 0 && (
                <text x={toX(i)} y={chartHeight - padding.bottom + 16} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="9">
                  {d.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {hasSecondLine && (
        <div className="flex items-center gap-4 mt-2 ml-14 text-[10px] text-white font-medium">
          <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 rounded" style={{ backgroundColor: color }} /> {label1}</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 rounded border-dashed" style={{ backgroundColor: color2 }} /> {label2}</span>
        </div>
      )}
    </div>
  );
}
